import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import {
  EMPTY_STAGE_FORM,
  FORECAST_CATEGORIES,
  formFromStage,
  normalizeHexColor,
  toStageOption,
} from './stageMasterUtils';

/**
 * Add or edit a stage_master row from Setup → Probability Manager.
 */
const EditStageMasterModal = ({
  show,
  onHide,
  mode = 'create',
  stage = null,
  stageOptions = [],
  onSave,
  saving = false,
}) => {
  const isEdit = mode === 'edit' && stage != null;
  const [form, setForm] = useState(EMPTY_STAGE_FORM);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setForm(isEdit ? formFromStage(stage) : { ...EMPTY_STAGE_FORM });
    setError('');
  }, [show, isEdit, stage]);

  const stageOptionsWithCurrent = useMemo(() => {
    const current = String(form.name || '').trim();
    if (!current) return stageOptions;
    const exists = stageOptions.some((option) => option.value === current);
    return exists ? stageOptions : [toStageOption(current, current), ...stageOptions];
  }, [form.name, stageOptions]);

  const onChange = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'is_won' && value) {
        next.is_closed = true;
        next.forecast_category = 'Closed';
      }
      if (key === 'is_closed' && !value) {
        next.is_won = false;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const payload = {
      name: String(form.name || '').trim(),
      default_probability: Number(form.default_probability),
      sequence_order: Number(form.sequence_order),
      forecast_category: form.forecast_category || 'Pipeline',
      is_closed: Boolean(form.is_closed),
      is_won: Boolean(form.is_won),
      color: normalizeHexColor(form.color),
    };

    if (!payload.name) {
      setError('Stage name is required.');
      return;
    }
    if (
      Number.isNaN(payload.default_probability) ||
      payload.default_probability < 0 ||
      payload.default_probability > 100
    ) {
      setError('Default probability must be between 0 and 100.');
      return;
    }
    if (!Number.isInteger(payload.sequence_order) || payload.sequence_order <= 0) {
      setError('Sequence order must be a positive integer.');
      return;
    }
    if (!FORECAST_CATEGORIES.includes(payload.forecast_category)) {
      setError('Please select a valid forecast category.');
      return;
    }
    if (payload.is_won) {
      payload.is_closed = true;
      payload.forecast_category = 'Closed';
    }

    try {
      await onSave({ payload, editingId: isEdit ? stage.id : null });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save stage.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit Stage' : 'Add Stage'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error ? (
            <div className="text-danger mb-3" role="alert">
              {error}
            </div>
          ) : null}

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Stage Name *</Form.Label>
                <Form.Select
                  value={form.name}
                  onChange={(e) => onChange('name', e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select stage</option>
                  {stageOptionsWithCurrent.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Default Probability (%) *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.default_probability}
                  onChange={(e) => onChange('default_probability', e.target.value)}
                  disabled={saving}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Sequence Order *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  step="1"
                  value={form.sequence_order}
                  onChange={(e) => onChange('sequence_order', e.target.value)}
                  disabled={saving}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Forecast Category *</Form.Label>
                <Form.Select
                  value={form.forecast_category}
                  onChange={(e) => onChange('forecast_category', e.target.value)}
                  disabled={saving || form.is_won}
                >
                  {FORECAST_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Color</Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="color"
                    value={normalizeHexColor(form.color)}
                    onChange={(e) => onChange('color', e.target.value)}
                    disabled={saving}
                    style={{ width: '42px', height: '38px', padding: '2px' }}
                  />
                  <Form.Control
                    type="text"
                    value={form.color}
                    onChange={(e) => onChange('color', e.target.value)}
                    disabled={saving}
                    placeholder="#d9d9d9"
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="stage-is-closed"
                label="Closed"
                checked={Boolean(form.is_closed)}
                onChange={(e) => onChange('is_closed', e.target.checked)}
                disabled={saving || form.is_won}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="stage-is-won"
                label="Won"
                checked={Boolean(form.is_won)}
                onChange={(e) => onChange('is_won', e.target.checked)}
                disabled={saving}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} type="button" disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditStageMasterModal;
